import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import TextField from '@mui/material/TextField';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import MetaData from '../Layouts/MetaData';
import BackdropLoader from '../Layouts/BackdropLoader';

const VendorForm = () => {
    const { id } = useParams();
    const isEditMode = Boolean(id);
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [loading, setLoading] = useState(isEditMode);
    const [saving, setSaving] = useState(false);

    const [name, setName] = useState("");
    const [brandName, setBrandName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [password, setPassword] = useState("");

    useEffect(() => {
        if (!isEditMode) return;
        const fetchVendor = async () => {
            try {
                const { data } = await axios.get(`/api/v1/admin/vendor/${id}`);
                setName(data.vendor.name);
                setBrandName(data.vendor.brandName || "");
                setEmail(data.vendor.email);
                setPhone(data.vendor.phone || "");
            } catch (error) {
                enqueueSnackbar(error?.response?.data?.message || "Failed to load vendor", { variant: "error" });
                navigate('/admin/vendors');
            } finally {
                setLoading(false);
            }
        };
        fetchVendor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const submitHandler = async (e) => {
        e.preventDefault();

        if (!isEditMode && (!password || password.length < 6)) {
            enqueueSnackbar("Please set a password (at least 6 characters)", { variant: "error" });
            return;
        }

        setSaving(true);
        try {
            if (isEditMode) {
                await axios.put(`/api/v1/admin/vendor/${id}`, { name, brandName, email, phone });
                enqueueSnackbar("Vendor updated", { variant: "success" });
            } else {
                await axios.post('/api/v1/admin/vendor/new', { name, brandName, email, phone, password });
                enqueueSnackbar("Vendor created — a welcome email has been sent", { variant: "success" });
            }
            navigate('/admin/vendors');
        } catch (error) {
            enqueueSnackbar(error?.response?.data?.message || "Failed to save vendor", { variant: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <BackdropLoader />;

    return (
        <>
            <MetaData title={`Admin: ${isEditMode ? 'Edit' : 'New'} Vendor | 1313 Mart`} />

            <Link to="/admin/vendors" className="flex items-center gap-0 font-medium text-primary-blue uppercase w-fit mb-4">
                <ArrowBackIosIcon sx={{ fontSize: "18px" }} />Back to Vendors
            </Link>

            <form onSubmit={submitHandler} className="bg-white rounded-xl shadow-lg p-6 flex flex-col gap-4 max-w-lg">
                <h1 className="text-lg font-medium">{isEditMode ? 'Edit Vendor' : 'New Vendor'}</h1>

                <TextField label="Vendor Name" size="small" required value={name} onChange={(e) => setName(e.target.value)} />
                <TextField label="Brand Name" size="small" value={brandName} onChange={(e) => setBrandName(e.target.value)} />
                <TextField label="Email" type="email" size="small" required value={email} onChange={(e) => setEmail(e.target.value)} />
                <TextField label="Phone" size="small" value={phone} onChange={(e) => setPhone(e.target.value)} />

                {!isEditMode && (
                    <TextField
                        label="Password"
                        size="small"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        helperText="At least 6 characters — this, along with the portal link, will be emailed to the vendor automatically."
                    />
                )}

                <button type="submit" disabled={saving} className="bg-primary-orange py-2.5 px-6 text-white font-medium rounded shadow hover:shadow-lg w-fit disabled:opacity-60">
                    {saving ? "Saving..." : isEditMode ? "Save Changes" : "Create Vendor"}
                </button>
            </form>
        </>
    );
};

export default VendorForm;